import { useContext } from 'react'
import { Link } from 'react-router-dom'

import DataItem from './DataItem'
import { getTool, getTune } from '../../../utils/uploads'
import BrandingContext from '../../../context/BrandingContext'
import { formatUploadCreditCost } from './creditCost'

const toYN = (value) => (value ? 'Yes' : 'No')

const toYPN = (value, meta) => {
  return `${value ? `Yes (${meta || 'None entered'})` : 'No'}`
}

const getBuildType = (analysisType, isAutoDtcOnly) => {
  if (analysisType !== 'AUTO') {
    return 'Manual'
  }

  return isAutoDtcOnly ? 'Auto (DTC only)' : 'Auto'
}

const hasSelectedUploadMods = (mods) => {
  return Object.values(mods).some(
    (value) => typeof value === 'boolean' && value,
  )
}

const isAutoModSelected = (auto, modId) => {
  return auto?.selectedMods?.includes(modId) || false
}

const ViewUploadData = ({ upload }) => {
  const {
    analysisType,
    uploadType,
    originalUpload,
    vehicle,
    vehicleType,
    manualVehicle,
    vrm,
    reference,
    mileage,
    tune,
    mods,
    priority,
    analysedFile,
  } = upload

  const [branding] = useContext(BrandingContext)
  const { primaryColorPalette } = branding
  const carMake = vehicle?.make || manualVehicle?.make
  const carModel = vehicle?.model || manualVehicle?.model
  const carFuel = vehicle?.fuel || manualVehicle?.fuel
  const carEngineSize = vehicle?.engineSize || manualVehicle?.engineSize
  const carStartYear = vehicle?.startYear || manualVehicle?.year
  const carEndYear = vehicle?.endYear

  const isAutoBuild = analysisType === 'AUTO'
  const autoBuild = analysedFile?.auto
  const removedDTCs = autoBuild?.removedDTCs || []
  const autoAvailableMods = autoBuild?.availableMods || []
  const isAutoDtcOnly =
    isAutoBuild && removedDTCs.length > 0 && !hasSelectedUploadMods(mods)
  const buildType = getBuildType(analysisType, isAutoDtcOnly)
  const removedDTCsValue = removedDTCs.join(', ')

  return (
    <div className="divide-y divide-gray-200">
      <div className="pt-6 pb-4 pl-6">
        <h4 className="font-semibold tracking-tight text-lg mb-0.5">
          Upload type
        </h4>
        <p className="text-gray-600 text-sm">
          Whether this is a full or adjustment remap
          {tune.tuneRequired === 'STAGE3' && ' and the stage 3 credit cost'}
        </p>
        <div className="divide-y divide-gray-200 mt-2">
          <DataItem
            label="Upload type"
            value={uploadType === 'FULL' ? 'Full remap' : 'Adjustment remap'}
          />
          <DataItem label="Build type" value={buildType} />
          {uploadType === 'ADJUST' && (
            <DataItem
              label="Original upload"
              renderValue={() => (
                <Link
                  className={`text-${primaryColorPalette}-600 hover:underline`}
                  to={`/uploads/view/${originalUpload.id}`}
                >
                  {originalUpload.uploadNumber}
                </Link>
              )}
            />
          )}
          {!isAutoBuild && (
            <DataItem label="Priority file" value={toYN(priority)} />
          )}
          {tune.tuneRequired === 'STAGE3' && (
            <DataItem
              label="Stage 3 credit cost"
              value={
                upload.stage3CreditCost
                  ? `${upload.stage3CreditCost} credit(s)`
                  : 'To be agreed'
              }
            />
          )}
          {mods.immoOff && (
            <DataItem
              label="Immobiliser removal credit cost"
              value={formatUploadCreditCost(
                upload.immoOffCreditCost,
                'To be agreed',
              )}
            />
          )}
        </div>
      </div>
      <div className="pt-6 pb-4 pl-6">
        <h4 className="font-semibold tracking-tight text-lg mb-0.5">
          Vehicle information
        </h4>
        <p className="text-gray-600 text-sm">
          Details on the vehicle from our database
        </p>
        <div className="divide-y divide-gray-200 mt-2">
          <DataItem label="Vehicle type" value={vehicleType} />
          {reference && <DataItem label="Reference" value={reference} />}
          {vrm && <DataItem label="VRM" value={vrm} />}
          {vehicleType === 'CAR' && (
            <>
              <DataItem label="Make" value={carMake} />
              <DataItem label="Model" value={carModel} />
              <DataItem label="Fuel" value={carFuel} />
              <DataItem
                label="Engine size"
                value={
                  carEngineSize
                    ? `${carEngineSize}${vehicle?.engineSize ? 'cc' : ''}`
                    : ''
                }
              />
              <DataItem
                label={carEndYear ? 'Start year' : 'Year'}
                value={carStartYear}
              />
              {carEndYear && <DataItem label="End year" value={carEndYear} />}
              <DataItem label="Mileage" value={mileage.toLocaleString()} />
              {vehicle?.vehicleId && (
                <DataItem label="Vehicle ID" value={vehicle.vehicleId} />
              )}
            </>
          )}
          {vehicleType !== 'CAR' && (
            <>
              <DataItem label="Make" value={manualVehicle.make} />
              <DataItem label="Model" value={manualVehicle.model} />
              <DataItem label="Engine size" value={manualVehicle.engineSize} />
              <DataItem label="HP model" value={manualVehicle.hpModel} />
              <DataItem label="Year" value={manualVehicle.year} />
              <DataItem label="Fuel" value={manualVehicle.fuel} />
            </>
          )}
        </div>
      </div>
      <div className="pt-6 pb-4 pl-6">
        <h4 className="font-semibold tracking-tight text-lg mb-0.5">
          ECU/Tune required
        </h4>
        <p className="text-gray-600 text-sm">
          Details on ECU, tuning tool and tune required
        </p>
        <div className="divide-y divide-gray-200 mt-2">
          <DataItem label="ECU type" value={tune.ecuType} />
          <DataItem label="Tuning tool used" value={getTool(tune.tuningTool)} />
          {tune.tuningTool === 'OTHER' && (
            <DataItem
              label="Tuning tool (other)"
              value={tune.tuningToolOther}
            />
          )}
          <DataItem label="Tune required" value={getTune(tune.tuneRequired)} />
          {!isAutoDtcOnly && (
            <DataItem
              label="Tune required details"
              value={tune.tuneRequiredCustom}
            />
          )}
        </div>
      </div>
      {isAutoBuild && autoAvailableMods.length > 0 && (
        <div className="pt-6 pb-4 pl-6">
          <h4 className="font-semibold tracking-tight text-lg mb-0.5">
            Automatic mods
          </h4>
          <p className="text-gray-600 text-sm">
            Automatic mod options shown during build and whether they were
            selected
          </p>
          <div className="divide-y divide-gray-200 mt-2">
            {autoAvailableMods.map((mod) => (
              <DataItem
                key={`${mod.id}-${mod.modName}`}
                label={mod.modName}
                value={toYN(isAutoModSelected(autoBuild, mod.id))}
              />
            ))}
          </div>
        </div>
      )}
      {isAutoBuild && removedDTCs.length > 0 && (
        <div className="pt-6 pb-4 pl-6">
          <h4 className="font-semibold tracking-tight text-lg mb-0.5">
            DTC options
          </h4>
          <div className="divide-y divide-gray-200 mt-2">
            <DataItem label="DTCs removed" value={removedDTCsValue} />
          </div>
        </div>
      )}
      {!isAutoBuild && (
        <div className="pt-6 pb-4 pl-6">
          <h4 className="font-semibold tracking-tight text-lg mb-0.5">
            Modifications
          </h4>
          <p className="text-gray-600 text-sm">
            Modifications made to the vehicle and any P codes
          </p>
          <div className="divide-y divide-gray-200 mt-2">
            {vehicle?.fuel === 'DIESEL' || manualVehicle?.fuel === 'DIESEL' ? (
              <>
                <DataItem
                  label="DPF off"
                  value={toYPN(mods.dpf, mods.dpfPCodes)}
                />
                <DataItem
                  label="EGR off"
                  value={toYPN(mods.egr, mods.egrPCodes)}
                />
                <DataItem
                  label="Swirl flap off"
                  value={toYPN(mods.swirlFlap, mods.swirlFlapPCodes)}
                />
                <DataItem
                  label="Hardcut limiter"
                  value={toYPN(mods.hardcut, `${mods.hardcutRpm} RPM`)}
                />
                <DataItem
                  label="Adblue delete"
                  value={toYPN(mods.adblue, mods.adbluePCodes)}
                />
                <DataItem
                  label="EGT off"
                  value={toYPN(mods.egt, mods.egtPCodes)}
                />
                <DataItem label="VMAX off" value={toYN(mods.vmax)} />
                <DataItem
                  label="Static rev limiter raised"
                  value={toYN(mods.staticRevLimiterRaised)}
                />
                <DataItem label="Deep idle" value={toYN(mods.deepIdle)} />
                <DataItem label="Smoke map" value={toYN(mods.smokeMap)} />
              </>
            ) : (
              <>
                <DataItem
                  label="O2 off"
                  value={toYPN(mods.o2, mods.o2PCodes)}
                />
                <DataItem
                  label="Swirl flap off"
                  value={toYPN(mods.swirlFlap, mods.swirlFlapPCodes)}
                />
                <DataItem
                  label="PPF/OPF/GPF off"
                  value={toYPN(mods.ppf, mods.ppfPCodes)}
                />
                <DataItem
                  label="Pops and bangs"
                  value={toYN(mods.popsAndBangs)}
                />
                <DataItem label="VMAX off" value={toYN(mods.vmax)} />
                <DataItem
                  label="Rev limiter raised"
                  value={toYN(mods.revLimiter)}
                />
                <DataItem
                  label="Static rev limiter raised"
                  value={toYN(mods.staticRevLimiterRaised)}
                />
                <DataItem
                  label="Hardcut limiter"
                  value={toYPN(mods.hardcut, `${mods.hardcutRpm} RPM`)}
                />
              </>
            )}
            <DataItem label="Immobiliser removal" value={toYN(mods.immoOff)} />
          </div>
        </div>
      )}
    </div>
  )
}

export default ViewUploadData
